import type { ReactNode, RefObject } from 'react';
import { Building2Icon } from 'lucide-react';
import { Button, Card, EmptyState, ErrorState, Skeleton, Spinner } from '@opengoal/ui';
import { useTranslation } from 'react-i18next';
import { useInfiniteScroll } from '@/shared/hooks/useInfiniteScroll';
import { errorMessage } from '@/shared/lib/errors';
import type { AgencyClubDto } from '@/api/generated/model';
import { ClubCard } from './ClubCard';

export interface ClubGridProps {
  clubs: AgencyClubDto[];
  /** True only for the first load; a page appended later is not "loading". */
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  onLoadMore: () => void;
  error: unknown;
  /** Re-runs the whole query. For a first load that failed outright. */
  onRetry: () => void;
  /** Whether a search or relationship filter is active, which changes the empty copy. */
  isFiltered: boolean;
  onOpen: (club: AgencyClubDto) => void;
  /** Omit both to render read-only cards — see `ClubCardProps`. */
  onEdit?: (club: AgencyClubDto) => void;
  onRemove?: (club: AgencyClubDto) => void;
  emptyAction?: ReactNode;
  /**
   * Replaces both empty messages. The defaults are written for the clubs page
   * ("add your first club"), which is the wrong thing to say on a screen where
   * the grid is a filtered view rather than the whole network.
   */
  emptyDescription?: ReactNode;
  /**
   * The element the grid scrolls inside, when it is not the page. Without it
   * the load-more sentinel is measured against the viewport and the next page
   * arrives late.
   */
  scrollRoot?: RefObject<Element | null>;
}

const GRID_CLASS = 'grid grid-cols-[repeat(auto-fill,minmax(min(20rem,100%),1fr))] gap-4';

/**
 * The card grid, plus every state it can be in.
 *
 * This is the grid equivalent of `<DataTable>`: loading, empty and error are
 * handled here once rather than re-decided by each page. A grid that renders
 * nothing when the request 500s is the same lie as an empty table.
 *
 * The failure case worth noticing is an error *while cards are already on
 * screen* — a page that failed to append. Replacing the whole grid with an
 * error panel would throw away everything the user had scrolled through, so
 * that case gets a retry strip under the last row instead.
 */
export function ClubGrid({
  clubs,
  isLoading,
  isFetchingNextPage,
  hasNextPage,
  onLoadMore,
  error,
  onRetry,
  isFiltered,
  onOpen,
  onEdit,
  onRemove,
  emptyAction,
  emptyDescription,
  scrollRoot,
}: ClubGridProps) {
  const { t } = useTranslation();

  const sentinelRef = useInfiniteScroll({
    hasMore: hasNextPage && !error,
    isLoading: isFetchingNextPage || isLoading,
    onLoadMore,
    ...(scrollRoot ? { root: scrollRoot } : {}),
  });

  if (isLoading) return <ClubGridSkeleton />;

  if (error && clubs.length === 0) {
    return (
      <Card>
        <ErrorState message={errorMessage(error)} onRetry={onRetry} />
      </Card>
    );
  }

  if (clubs.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={<Building2Icon />}
          description={
            emptyDescription ??
            (isFiltered ? t('clubs.empty.filtered') : t('clubs.empty.description'))
          }
          action={isFiltered ? undefined : emptyAction}
        />
      </Card>
    );
  }

  return (
    <div className="grid gap-2">
      <div className={GRID_CLASS}>
        {clubs.map((club) => (
          <ClubCard key={club.id} club={club} onOpen={onOpen} onEdit={onEdit} onRemove={onRemove} />
        ))}
      </div>

      {/* Sits below the last row; scrolling it into view fetches the next page. */}
      <div ref={sentinelRef} aria-hidden className="h-px" />

      <div className="flex justify-center py-6">
        {error ? (
          <div className="grid justify-items-center gap-2">
            <p className="text-sm text-muted-foreground">{errorMessage(error)}</p>
            {/* `onLoadMore`, not `onRetry`: the page that failed is the *next*
                one, and refetching the query would re-request the pages that
                already succeeded while never retrying the one that didn't. */}
            <Button variant="outline" size="sm" onClick={onLoadMore}>
              {t('actions.retry')}
            </Button>
          </div>
        ) : isFetchingNextPage ? (
          <Spinner />
        ) : hasNextPage ? (
          // A manual fallback for anyone whose browser never fires the observer
          // — a keyboard user tabbing through, or a pinned-zoom viewport where
          // the sentinel is already inside the root margin.
          <Button variant="outline" size="sm" onClick={onLoadMore}>
            {t('clubs.loadMore')}
          </Button>
        ) : (
          <p className="text-[13px] text-muted-foreground">
            {t('clubs.endOfList', { total: clubs.length })}
          </p>
        )}
      </div>
    </div>
  );
}

/** Placeholder cards at roughly the height of a real one, to avoid a jump. */
function ClubGridSkeleton() {
  return (
    <div className={GRID_CLASS}>
      {Array.from({ length: 6 }, (_, index) => (
        <Card key={index} className="gap-3 px-4">
          <div className="flex items-center gap-3">
            <Skeleton className="size-11 rounded-full" />
            <div className="grid flex-1 gap-2">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-3 w-1/3" />
            </div>
          </div>
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-3 w-1/2" />
        </Card>
      ))}
    </div>
  );
}
